import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const NotificationSystem = ({
  showNotification,
  currentNotification,
  handleNotificationClose
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  // Timer for auto-dismiss
  useEffect(() => {
    let dismissTimer;
    if (showNotification && currentNotification?.duration) {
      dismissTimer = setTimeout(() => {
        handleNotificationClose(null, 'timeout');
      }, currentNotification.duration);
    }
    
    return () => {
      if (dismissTimer) clearTimeout(dismissTimer);
    };
  }, [showNotification, currentNotification, handleNotificationClose]);

  // Enhanced visibility control
  useEffect(() => {
    if (showNotification) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [showNotification]);

  // Extract tracking ID from success messages
  const extractTrackingId = (message) => {
    const trackingMatch = message?.match(/Tracking ID:\s*(TK-[A-Z0-9]+)/);
    return trackingMatch ? trackingMatch[1] : null;
  };

  // Handle view ticket navigation
  const handleViewTicket = () => {
    const trackingId = extractTrackingId(currentNotification?.message);
    if (trackingId) {
      handleNotificationClose();
      navigate('/track-ticket', { 
        state: { 
          prefilledTrackingId: trackingId,
          fromNotification: true 
        } 
      });
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  // Get notification colors
  const getNotificationColors = (type) => {
    switch (type) {
      case 'success':
        return {
          primary: 'text-green-600',
          secondary: 'text-green-800',
          background: 'bg-green-50',
          border: 'border-green-200',
          button: 'bg-green-600 hover:bg-green-700'
        };
      case 'error':
        return {
          primary: 'text-red-600',
          secondary: 'text-red-800',
          background: 'bg-red-50',
          border: 'border-red-200',
          button: 'bg-red-600 hover:bg-red-700'
        };
      case 'warning':
        return {
          primary: 'text-yellow-600',
          secondary: 'text-yellow-800',
          background: 'bg-yellow-50',
          border: 'border-yellow-200',
          button: 'bg-yellow-600 hover:bg-yellow-700'
        };
      default:
        return {
          primary: 'text-blue-600',
          secondary: 'text-blue-800',
          background: 'bg-blue-50',
          border: 'border-blue-200',
          button: 'bg-blue-600 hover:bg-blue-700'
        };
    }
  };

  const trackingId = extractTrackingId(currentNotification?.message);
  const colors = getNotificationColors(currentNotification?.type);
  const isSuccessWithTracking = currentNotification?.type === 'success' && trackingId;

  if (!showNotification) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`bg-white rounded-xl shadow-2xl overflow-hidden w-full max-w-sm sm:max-w-md max-h-[90vh] flex flex-col transform transition-all duration-300 ${
        isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}>
        {/* Header */}
        <div className={`p-4 border-b ${colors.border} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className={colors.primary}>
              {getNotificationIcon(currentNotification?.type)}
            </div>
            <h3 className={`font-semibold font-poppins text-sm ${colors.secondary}`}>
              {currentNotification?.type === 'success' ? 'Success' : 
               currentNotification?.type === 'error' ? 'Error' :
               currentNotification?.type === 'warning' ? 'Warning' : 'Info'}
            </h3>
          </div>
          <button
            onClick={handleNotificationClose}
            className="text-gray-600 hover:text-gray-800 p-1 rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Message content */}
        <div className="p-4">
          <p className="font-poppins text-gray-900 text-sm leading-relaxed">
            {currentNotification?.message}
          </p>
        </div>

        {/* Action buttons for success notifications with tracking ID */}
        {isSuccessWithTracking && (
          <div className="p-4 pt-0 flex justify-start">
            <button
              onClick={handleViewTicket}
              className={`px-4 py-2 text-white rounded-lg font-semibold font-poppins text-sm transition-colors flex items-center gap-2 ${colors.button}`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              View Ticket {trackingId}
            </button>
          </div>
        )}

        {/* Progress bar for auto-dismiss */}
        {currentNotification?.duration && (
          <div className="h-1 bg-gray-200 relative overflow-hidden">
            <div 
              className={`h-full ${colors.primary.replace('text-', 'bg-')} animate-pulse`}
              style={{
                animation: `shrink ${currentNotification.duration}ms linear forwards`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSystem;